import { API_BASE_URL } from "../config/api";

const PAID_REVENUE_STATUSES = ["Ongoing Project", "Finished Project"];
const PIPELINE_STATUSES = ["Pending", "Incomplete Booking", "In Review", "Interested", "Request for Callback"];

function parseBudgetValue(projectBudget) {
  if (!projectBudget || projectBudget === "not_sure") {
    return 0;
  }

  const rangeValues = String(projectBudget).match(/[0-9]+(?:\.[0-9]+)?/g)?.map(Number) || [];
  if (rangeValues.length > 1) {
    return (rangeValues[0] + rangeValues[1]) / 2;
  }

  const normalizedValue = String(projectBudget).replace(/[^0-9.]/g, "");
  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
}

export function normalizeConsultation(record) {
  const bookedAt = record.bookedAt || new Date().toISOString();
  const fallbackDate = bookedAt.split("T")[0];
  const rawStatus = record.status || "Pending";
  const normalizedStatus =
    rawStatus === "Confirmed" || rawStatus === "Closed Deal" || rawStatus === "Project in Progress"
      ? "Ongoing Project"
      : rawStatus;
  const leadSource =
    record.leadSource ||
    (record.leadType === "manual"
      ? "Manual Entry"
      : record.consultationType === "Phone Call"
        ? "Phone Inquiry"
        : "Lead Form");
  const derivedProjectValue =
    record.projectValue !== undefined && record.projectValue !== null && record.projectValue !== ""
      ? Number(record.projectValue || 0)
      : parseBudgetValue(record.projectBudget);

  return {
    id: "",
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    companySize: "",
    industry: "",
    serviceType: "",
    consultationType: "Video Call",
    preferredDate: "",
    preferredTime: "",
    preferredTimezone: "",
    projectBudget: "",
    message: "",
    status: normalizedStatus,
    bookedAt,
    leadType: "booking",
    leadSource,
    attributionBlogSlug: "",
    projectName: record.serviceType || "",
    projectValue: 0,
    downpayment: 0,
    monthlyPayment: 0,
    projectDurationMonths: 0,
    lastContact: fallbackDate,
    createdBy: "",
    payments: [],
    emailEvents: [],
    journeyEvents: [],
    newsletterSentCount: 0,
    newsletterLastSentAt: "",
    newsletterOpenCount: 0,
    newsletterLastOpenedAt: "",
    newsletterOpenCountryCode: "",
    newsletterOpenCountry: "",
    newsletterScheduleClickCount: 0,
    newsletterLastScheduleClickAt: "",
    newsletterNotInterestedClickCount: 0,
    newsletterLastNotInterestedClickAt: "",
    newsletterLastJourneyAt: "",
    ...record,
    status: normalizedStatus,
    bookedAt,
    leadSource,
    projectName: record.projectName || record.serviceType || "",
    projectValue: derivedProjectValue,
    downpayment: Number(record.downpayment || 0),
    monthlyPayment: Number(record.monthlyPayment || 0),
    projectDurationMonths: Number(record.projectDurationMonths || 0),
    lastContact: record.lastContact || fallbackDate,
    createdBy: record.createdBy || "",
    payments: Array.isArray(record.payments)
      ? record.payments.map((payment) => ({
          ...payment,
          transactionAttachment: payment.transactionAttachment || ""
        }))
      : [],
    emailEvents: Array.isArray(record.emailEvents) ? record.emailEvents : [],
    journeyEvents: Array.isArray(record.journeyEvents) ? record.journeyEvents : [],
    newsletterSentCount: Number(record.newsletterSentCount || 0),
    newsletterLastSentAt: record.newsletterLastSentAt || "",
    newsletterOpenCount: Number(record.newsletterOpenCount || 0),
    newsletterLastOpenedAt: record.newsletterLastOpenedAt || "",
    newsletterScheduleClickCount: Number(record.newsletterScheduleClickCount || 0),
    newsletterLastScheduleClickAt: record.newsletterLastScheduleClickAt || "",
    newsletterNotInterestedClickCount: Number(record.newsletterNotInterestedClickCount || 0),
    newsletterLastNotInterestedClickAt: record.newsletterLastNotInterestedClickAt || "",
    newsletterLastJourneyAt: record.newsletterLastJourneyAt || ""
  };
}

async function parseResponse(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || "Request failed.");
  }
  return data;
}

export async function fetchConsultations(authToken) {
  const response = await fetch(`${API_BASE_URL}/consultations`, {
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  const data = await parseResponse(response);
  return Array.isArray(data) ? data.map(normalizeConsultation) : [];
}

export async function fetchInbox(authToken, limit = 40) {
  const response = await fetch(`${API_BASE_URL}/inbox?limit=${limit}`, {
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  return parseResponse(response);
}

export async function markInboxMessageRead(authToken, messageId) {
  const response = await fetch(`${API_BASE_URL}/inbox/${encodeURIComponent(messageId)}/read`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  return parseResponse(response);
}

export async function searchPublicProspects(authToken, criteria) {
  const response = await fetch(`${API_BASE_URL}/prospects/search`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`
    },
    body: JSON.stringify(criteria)
  });
  return parseResponse(response);
}

export async function createConsultation(consultation, authToken = "") {
  const response = await fetch(`${API_BASE_URL}/consultations`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
    },
    body: JSON.stringify(normalizeConsultation(consultation))
  });
  return normalizeConsultation(await parseResponse(response));
}

export async function deleteAllConsultations(authToken) {
  const response = await fetch(`${API_BASE_URL}/consultations`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  return parseResponse(response);
}

export async function deleteConsultation(consultationId, authToken) {
  const response = await fetch(`${API_BASE_URL}/consultations/${consultationId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  return parseResponse(response);
}

export async function updateConsultation(consultation, authToken) {
  const response = await fetch(`${API_BASE_URL}/consultations/${consultation.id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`
    },
    body: JSON.stringify(normalizeConsultation(consultation))
  });
  return normalizeConsultation(await parseResponse(response));
}

export async function fetchNewsletterTemplates(authToken) {
  const response = await fetch(`${API_BASE_URL}/newsletter-templates`, {
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  return parseResponse(response);
}

export async function fetchNewsletterTemplateContent(authToken, templatePath) {
  const response = await fetch(`${API_BASE_URL}/newsletter-templates/${encodeURIComponent(templatePath)}/content`, {
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  return parseResponse(response);
}

export async function updateNewsletterTemplateContent(authToken, templatePath, bodyHtml) {
  const response = await fetch(`${API_BASE_URL}/newsletter-templates/${encodeURIComponent(templatePath)}/content`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`
    },
    body: JSON.stringify({
      bodyHtml
    })
  });
  return parseResponse(response);
}

export async function sendConsultationNewsletter(consultationId, authToken, templatePath, subject, blogSlug = "", youtubeUrl = "") {
  const response = await fetch(`${API_BASE_URL}/consultations/${consultationId}/send-newsletter`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`
    },
    body: JSON.stringify({
      templatePath,
      subject,
      blogSlug,
      youtubeUrl
    })
  });
  return parseResponse(response);
}

export async function fetchNewsletterBookingPrefill(token) {
  const response = await fetch(`${API_BASE_URL}/public/consultations/newsletter-booking?token=${encodeURIComponent(token)}`);
  return parseResponse(response);
}

export async function submitNewsletterBooking(token, consultation) {
  const response = await fetch(`${API_BASE_URL}/public/consultations/newsletter-booking`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      token,
      ...consultation
    })
  });
  return parseResponse(response);
}

export async function trackNewsletterJourneyEvent({ token, sessionId, eventType, pagePath = "", metadata = null }) {
  const response = await fetch(`${API_BASE_URL}/public/consultations/newsletter-event`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      token,
      sessionId,
      eventType,
      pagePath,
      metadata
    })
  });
  return parseResponse(response);
}

export { PAID_REVENUE_STATUSES, PIPELINE_STATUSES };
